import type {
  DrillDownCardElement,
  QueueCardElement,
  TextBlockElement,
} from '../../../demo-elements/types'
import type { DemoChatScript } from '../../../types'

const PNL_QUEUE: QueueCardElement = {
  kind: 'queue-card',
  id: 'pnl-brand-table',
  title: 'Cascade 3PL — Customer P&L Forecast — May 25–29 open orders',
  kpis: [
    {
      label: 'Open orders',
      value: '134,200',
      sub: '4 facilities · 68 brands',
      tone: 'neutral',
    },
    {
      label: 'Forecast revenue',
      value: '$3.12M',
      sub: 'rate-card priced',
      tone: 'good',
    },
    {
      label: 'Forecast contribution',
      value: '$485K · 15.6%',
      sub: 'after labor + ship + COGS',
      tone: 'good',
    },
    {
      label: 'Margin trajectory red',
      value: '1 of 68',
      sub: 'Northwind — largest customer',
      tone: 'critical',
    },
  ],
  columns: [
    { id: 'brand', label: 'Brand' },
    { id: 'orders', label: 'Open orders', align: 'right', compact: true },
    { id: 'revenue', label: 'Rev', align: 'right', compact: true },
    { id: 'margin', label: 'Margin', align: 'right', compact: true },
    { id: 'trend', label: '6-mo trend' },
  ],
  rows: [
    {
      id: 'pnl-northwind',
      status: 'critical',
      emphasize: true,
      cells: {
        brand: 'Northwind Apparel',
        orders: '28,400',
        revenue: '$441K',
        margin: { value: '7.2%', tone: 'critical' },
        trend: { value: '18.0% → 7.2%', tone: 'critical' },
      },
    },
    {
      id: 'pnl-briar',
      status: 'good',
      cells: {
        brand: 'Briar Coffee Roasters',
        orders: '18,200',
        revenue: '$289K',
        margin: { value: '17.5%', tone: 'good' },
        trend: '17.8% → 17.5%',
      },
    },
    {
      id: 'pnl-trailmix',
      status: 'good',
      cells: {
        brand: 'Trailmix Provisions',
        orders: '16,500',
        revenue: '$244K',
        margin: { value: '14.1%', tone: 'good' },
        trend: '14.3% → 14.1%',
      },
    },
    {
      id: 'pnl-oakhill',
      status: 'good',
      cells: {
        brand: 'OakHill Skincare',
        orders: '9,200',
        revenue: '$208K',
        margin: { value: '16.8%', tone: 'good' },
        trend: '15.2% → 16.8%',
      },
    },
    {
      id: 'pnl-altitude',
      status: 'good',
      cells: {
        brand: 'Altitude Supplements',
        orders: '11,400',
        revenue: '$184K',
        margin: { value: '12.9%', tone: 'good' },
        trend: '13.2% → 12.9%',
      },
    },
    {
      id: 'pnl-halcyon',
      status: 'good',
      cells: {
        brand: 'Halcyon Pet',
        orders: '7,800',
        revenue: '$159K',
        margin: { value: '14.5%', tone: 'good' },
        trend: '14.0% → 14.5%',
      },
    },
    {
      id: 'pnl-fernweh',
      status: 'warning',
      cells: {
        brand: 'Fernweh Outdoors',
        orders: '5,200',
        revenue: '$135K',
        margin: { value: '8.4%', tone: 'warning' },
        trend: { value: '11.1% → 8.4%', tone: 'warning' },
      },
    },
    {
      id: 'pnl-lumen',
      status: 'warning',
      cells: {
        brand: 'Lumen Candles',
        orders: '4,100',
        revenue: '$94K',
        margin: { value: '3.2%', tone: 'warning' },
        trend: { value: '8.6% → 3.2%', tone: 'warning' },
      },
    },
  ],
  footer:
    'Showing 8 of 68 brands by revenue. Northwind is the largest brand below the 10% margin floor; Fernweh + Lumen are tail accounts at 8.4% and 3.2%.',
}

const NORTHWIND_DRILL: DrillDownCardElement = {
  kind: 'drill-down-card',
  id: 'pnl-northwind-drill',
  title: 'Northwind Apparel · $22M/yr · largest customer by revenue',
  subtitle:
    '6-mo contribution margin: 18.0% → 12.4% → 9.1% → 7.2% · annualized contribution lost YTD: $2.4M',
  facts: [
    {
      label: 'Trajectory',
      value: '2.1% by Sep · break-even by Nov',
      tone: 'critical',
    },
    {
      label: 'Avg package weight',
      value: '1.4 lb → 1.9 lb (+38%)',
      tone: 'critical',
    },
    {
      label: 'Zone 7+ share',
      value: '12% → 35%',
      tone: 'critical',
    },
    {
      label: 'Avg ship cost / order',
      value: '$7.20 → $11.85 (+65%)',
      tone: 'critical',
    },
    {
      label: 'Driver',
      value: 'West-coast DTC launch · April',
    },
    {
      label: 'Contract',
      value: 'Q3 2024 rate card · expires Mar 2027',
    },
  ],
}

const DECISION_BLOCK: TextBlockElement = {
  kind: 'text-block',
  id: 'pnl-decision-block',
  text: `**Three paths, sized:**

**1. Renegotiate now — zone 5+ surcharge $2.50/order on ~38% of orders.**
Recovers to 13.5% margin. Annualized contribution back: +$1.4M. Risk: opening a contract 10 months early.

**2. Wait for Mar 2027 renewal.**
Accept ~$2.1M lost contribution over the next 10 months. Their west-coast growth keeps compounding.

**3. Cap west-coast volume at current run rate.**
Hold zone 7+ share at 35% until renewal. Northwind's growth path stalls; relationship risk.

Recommend Option 1. Their growth shows commitment to Cascade; the renegotiation is a number, not a fight.`,
}

const BRIEF_CARD: DrillDownCardElement = {
  kind: 'drill-down-card',
  id: 'pnl-brief-card',
  title: 'Renegotiation brief — Cascade × Northwind',
  subtitle:
    'Drafted for Jordan → Stephanie M. (Northwind COO) · queued for Mon revops review',
  facts: [
    { label: 'Subject', value: 'Zone 5+ surcharge proposal · effective Jun 1' },
    {
      label: 'Ask',
      value: '$2.50/order surcharge on zone 5+ deliveries (~38% of volume)',
    },
    {
      label: 'Data attached',
      value: '6-mo margin trend · weight + zone shift · cost-per-order delta',
    },
    {
      label: 'Frame',
      value: 'Preserves Northwind growth path + Cascade service levels',
    },
    { label: 'Evaluation window', value: '90-day review post-effective date' },
    { label: 'Storage', value: 'Drive · Cascade > Revops > Renegotiations Q2' },
  ],
  primaryAction: {
    label: 'Park brief in Drive · queue for Mon revops',
    context: "Reply lands in Jordan's inbox",
    preview:
      "Stephanie — over the last six months Northwind's contribution margin at Cascade has moved from 18% to 7.2%, driven primarily by the west-coast launch (zone 7+ share went from 12% to 35%, avg package weight up 38%). At the current trajectory we're forecasting break-even by November. We want to keep your growth path intact, so we'd like to propose a zone 5+ surcharge of $2.50/order effective Jun 1, with a 90-day review window. Affects ~38% of your orders. Happy to walk through the math any time this week.",
    accent: 'email',
  },
}

const BEAT1_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'pnl-beat1-closer',
  text: 'Northwind is the largest brand by revenue and the only top-3 customer below the 10% margin floor. **Drill in.**',
}

const BEAT2_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'pnl-beat2-closer',
  text: '$2.4M of annualized contribution already lost. **Draft the renegotiation brief?**',
}

const BEAT3_WRAP_CUE: TextBlockElement = {
  kind: 'text-block',
  id: 'pnl-wrap-cue',
  text: "**Want to see how next week's labor shapes up?**",
}

/**
 * Cascade 3PL — Demo 1: Customer P&L Forecast.
 *
 * Beat 1: Open-order P&L forecast for next week, 68 brands. Northwind
 * Apparel sits at the top by revenue and the bottom by margin trajectory.
 *
 * Beat 2: Drill into Northwind. Cause (west-coast launch + old rate card),
 * math (zone 7+ share, package weight, ship cost), trajectory (break-even
 * by November), three decision paths with sized impact.
 *
 * Beat 3: Draft the renegotiation brief. Park in Drive, queue for Mon
 * revops review. Reply routes to Jordan.
 */
export const customerPnlForecast: DemoChatScript = {
  id: 'customer-pnl-forecast',
  name: 'Customer P&L Forecast',
  description:
    "Demo 1. Per-customer profitability for the upcoming week — what the open order queue is about to do to Cascade's margin. The view your accountant gives you 30 days late, before the orders ship.",
  triggers: [
    'p&l',
    'pnl',
    'p and l',
    'p & l',
    'customer profitability',
    'margin forecast',
    'margin',
    'profit forecast',
    'next week margin',
    "next week's margin",
    "next week's p&l",
    'which customers',
    'which brands make money',
    'who is losing money',
    "who's losing money",
    'northwind',
    'northwind apparel',
    'open orders',
    'show me the p&l',
    'customer pnl',
    'customer p&l',
    'customer p&l forecast',
  ],
  followUps: [
    {
      triggers: [
        'yes',
        'yeah',
        'yep',
        'sure',
        'ok',
        'okay',
        'go',
        'go ahead',
        'do it',
        'please',
        'continue',
        'next',
        "what's next",
        'whats next',
        'show me',
        'show me more',
        'more',
        'keep going',
        'labor',
        'labor forecast',
        'staffing',
        'next week labor',
      ],
      nextScriptId: 'labor-forecast',
    },
    {
      triggers: ['claims', 'carrier claims', 'recovery'],
      nextScriptId: 'carrier-claims-agent',
    },
    {
      triggers: [
        'post-shipment',
        'post shipment',
        'delays',
        'customer service',
      ],
      nextScriptId: 'post-shipment-support',
    },
  ],
  events: [
    // ─────────── Beat 1: brand P&L table ───────────
    {
      type: 'status',
      delayMs: 200,
      message: "Pulling next week's open orders + rate-card costs...",
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        "134K open orders for May 25–29 across all 4 facilities. Pricing each at the brand's rate card; subtracting forecast labor + carrier + COGS at the order level.\n",
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'forecast_next_week_pnl' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        '$3.12M revenue, $485K contribution (15.6%). Largest brand by revenue is below the 10% margin floor.',
    },
    { type: 'demo-element-add', delayMs: 600, element: PNL_QUEUE },
    { type: 'demo-element-add', delayMs: 700, element: BEAT1_CLOSER },
    { type: 'done', delayMs: 400, title: 'Customer P&L — May 25–29' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2: Northwind drill + decision ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Pulling Northwind 6-month trajectory + zone mix...',
    },
    { type: 'tool-call', delayMs: 300, name: 'fetch_brand_pnl_history' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'Northwind: 28,400 open orders next week, 7.2% contribution. Trajectory points at break-even by November.',
    },
    { type: 'demo-element-add', delayMs: 600, element: NORTHWIND_DRILL },
    { type: 'demo-element-add', delayMs: 700, element: DECISION_BLOCK },
    { type: 'demo-element-add', delayMs: 700, element: BEAT2_CLOSER },
    { type: 'done', delayMs: 400, title: 'Northwind — drill + decision' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 3: draft renegotiation brief ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Drafting renegotiation brief for Jordan → Stephanie M...',
    },
    { type: 'tool-call', delayMs: 300, name: 'draft_renegotiation_brief' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'Draft pulls the 6-month margin trend, the zone + weight shift, the proposed surcharge, and a 90-day review window.',
    },
    { type: 'demo-element-add', delayMs: 600, element: BRIEF_CARD },
    {
      type: 'approval',
      delayMs: 700,
      proposal: {
        proposalId: 'demo-cascade-pnl-renegotiation-brief',
        toolName: 'draft_renegotiation_brief',
        actionType: 'document_draft',
        description:
          "Draft renegotiation brief for Northwind Apparel: zone 5+ surcharge $2.50/order, effective Jun 1, 90-day review window. Brief stored in Drive (Cascade > Revops > Renegotiations Q2); queued for Monday revops review. Reply to Stephanie M. routes to Jordan's inbox.",
      },
    },
    { type: 'done', delayMs: 400, title: 'Renegotiation brief — ready' },
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-cascade-pnl-renegotiation-brief',
    },

    // ─────────── Wrap: cue toward labor forecast ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Brief in Drive · Mon revops queue.',
    },
    {
      type: 'text-delta',
      delayMs: 400,
      delta: "Brief sits in Drive. Monday's revops review picks it up.",
    },
    { type: 'demo-element-add', delayMs: 500, element: BEAT3_WRAP_CUE },
    { type: 'done', delayMs: 400, title: 'Customer P&L demo — complete' },
  ],
}
